'use client';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import { useSiteConfig } from '@/contexts/SiteConfigContext';
import { defaultDisplayTextConfig, DisplayTextConfig } from '@/lib/display-text';
import { defaultMenuConfig, TopNavItem } from '@/lib/navigation-config';
import { sharedCatalogMenu, type CatalogMenuItem } from '@/lib/catalog-menu';
import { buildGroupedSubs, getMegaSubHref, getSubCategoryHref } from '@/lib/catalog/menu';
import { normalizeCatalogText } from '@/lib/catalog/guards';
import { normalizeVoiceSearchQuery } from '@/lib/products/voice-search-normalizer';
import AppIcon from '@/components/ui/AppIcon';

type MegaMenuItem = CatalogMenuItem;

type MegaGroup = {
  title: string;
  items: string[];
};

type MegaPresetCategory = {
  label: string;
  icon: string;
  keywords: string[];
};

type SearchSuggestItem = {
  id: string;
  name: string;
  slug: string;
  image: string | null;
  price: number;
};

type TopNavDropdownItem = {
  label: string;
  href: string;
  description?: string;
};

type TopNavDropdownVisual = {
  src: string;
  alt: string;
  caption: string;
};

const templateMegaMenuCategories: MegaMenuItem[] = sharedCatalogMenu;

const megaPresetCategories: MegaPresetCategory[] = [
  {
    label: 'CPU',
    icon: 'https://file.hstatic.net/200000420363/file/artboard_2.png',
    keywords: ['cpu', 'processor'],
  },
  {
    label: 'MAIN',
    icon: 'https://file.hstatic.net/200000420363/file/artboard_2.png',
    keywords: ['main', 'mainboard', 'motherboard'],
  },
  {
    label: 'RAM',
    icon: 'https://file.hstatic.net/200000420363/file/artboard_3.png',
    keywords: ['ram'],
  },
  {
    label: 'VGA',
    icon: 'https://file.hstatic.net/200000420363/file/artboard_2.png',
    keywords: ['vga', 'card', 'gpu'],
  },
  {
    label: 'SSD',
    icon: 'https://file.hstatic.net/200000420363/file/artboard_3.png',
    keywords: ['ssd'],
  },
  {
    label: 'PSU',
    icon: 'https://file.hstatic.net/200000420363/file/artboard_4.png',
    keywords: ['psu', 'nguồn', 'nguon'],
  },
  {
    label: 'TẢN',
    icon: 'https://file.hstatic.net/200000420363/file/artboard_4.png',
    keywords: ['tản', 'tan', 'cooler'],
  },
  {
    label: 'CASE',
    icon: 'https://file.hstatic.net/200000420363/file/artboard_4.png',
    keywords: ['case', 'thùng', 'vo may'],
  },
  {
    label: 'FAN',
    icon: 'https://file.hstatic.net/200000420363/file/artboard_7_copy.png',
    keywords: ['fan'],
  },
  {
    label: 'MÀN',
    icon: 'https://file.hstatic.net/200000420363/file/gear_a6c70aa18a234378925f39561e3eaf2e.png',
    keywords: ['màn', 'man', 'monitor', 'màn hình', 'man hinh'],
  },
];

function getCatalogIconName(label: string) {
  const normalized = normalizeCatalogText(label);
  if (normalized.includes('laptop')) return 'ComputerDesktopIcon';
  if (normalized.includes('pc')) return 'ComputerDesktopIcon';
  if (normalized.includes('cpu') || normalized.includes('mainboard')) return 'CpuChipIcon';
  if (normalized.includes('ram') || normalized.includes('ssd') || normalized.includes('hdd')) {
    return 'CircleStackIcon';
  }
  if (normalized.includes('man hinh')) return 'TvIcon';
  if (normalized.includes('case') || normalized.includes('nguon')) return 'BoltIcon';
  if (normalized.includes('phim') || normalized.includes('chuot')) return 'CursorArrowRaysIcon';
  if (normalized.includes('ban') || normalized.includes('ghe')) return 'RectangleGroupIcon';
  if (normalized.includes('mang')) return 'WifiIcon';
  if (normalized.includes('may in') || normalized.includes('ups')) return 'PrinterIcon';
  if (normalized.includes('camera') || normalized.includes('webcam')) return 'CameraIcon';
  return 'Squares2X2Icon';
}

function buildMegaGroups(category: MegaMenuItem): MegaGroup[] {
  const subs = Array.isArray(category.sub) ? category.sub.filter(Boolean) : [];
  const lowerLabel = category.label.toLowerCase();

  const fallback: MegaGroup[] = [
    { title: `Gợi ý ${category.label}`, items: subs.slice(0, 6) },
    {
      title: 'Danh mục nổi bật',
      items: ['Sản phẩm mới', 'Bán chạy', 'Giá tốt hôm nay', 'Combo tiết kiệm'],
    },
  ];

  if (!subs.length) return fallback;

  if (lowerLabel.includes('pc')) {
    return [
      { title: 'PC theo giá', items: subs.slice(0, 6) },
      { title: 'PC theo hãng', items: ['PC STAR', 'PC ASUS', 'PC GIGABYTE', 'PC MSI'] },
      { title: 'PC theo nhu cầu', items: ['Gaming', 'Đồ họa', 'AI', 'Mini', 'White Edition'] },
    ];
  }

  if (lowerLabel.includes('cpu')) {
    return [
      { title: 'CPU Intel', items: ['Core i3', 'Core i5', 'Core i7', 'Core i9', 'Core Ultra'] },
      { title: 'CPU AMD', items: ['Ryzen 3', 'Ryzen 5', 'Ryzen 7', 'Ryzen 9'] },
      { title: 'Mức giá', items: ['Dưới 3 triệu', '3 - 5 triệu', '5 - 10 triệu', 'Trên 10 triệu'] },
    ];
  }

  if (lowerLabel.includes('vga') || lowerLabel.includes('card')) {
    return [
      {
        title: 'NVIDIA GeForce',
        items: ['RTX 3050', 'RTX 4060', 'RTX 5060', 'RTX 5070', 'RTX 5080'],
      },
      { title: 'AMD Radeon', items: ['RX 6600', 'RX 7600', 'RX 7700 XT', 'RX 7800 XT'] },
      { title: 'Theo thương hiệu', items: ['ASUS', 'MSI', 'GIGABYTE', 'Colorful', 'Sapphire'] },
    ];
  }

  return [
    { title: `${category.label} nổi bật`, items: subs.slice(0, 6) },
    { title: 'Theo thương hiệu', items: ['ASUS', 'MSI', 'GIGABYTE', 'Corsair', 'Kingston'] },
    { title: 'Theo nhu cầu', items: ['Gaming', 'Văn phòng', 'Thiết kế', 'Hiệu năng cao'] },
  ];
}

function chunkList(items: string[], size: number) {
  const chunks: string[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

function toOptionalUrl(value?: string | null): string | null {
  const normalized = String(value || '').trim();
  return normalized ? normalized : null;
}

function getTopNavDropdownItems(item: TopNavItem): TopNavDropdownItem[] {
  const label = normalizeCatalogText(item?.label || '');

  if (label === 'xay dung cau hinh') {
    return [
      {
        label: 'Xây dựng cấu hình',
        href: '/xay-dung-cau-hinh',
        description: 'Tự chọn linh kiện để build bộ PC theo nhu cầu.',
      },
    ];
  }

  if (label === 'lich ve sinh may') {
    return [
      {
        label: 'Đăng ký lịch vệ sinh máy',
        href: '/lien-he',
        description: 'Đặt lịch vệ sinh, bảo dưỡng và kiểm tra thiết bị nhanh ngay trên website.',
      },
      {
        label: 'Hỗ trợ kỹ thuật',
        href: '/lien-he',
        description: 'Gửi yêu cầu kiểm tra lỗi, tình trạng máy hoặc cần tư vấn kỹ thuật.',
      },
    ];
  }

  return [];
}

function getTopNavDropdownVisual(item: TopNavItem): TopNavDropdownVisual | null {
  const label = normalizeCatalogText(item?.label || '');

  if (label === 'xay dung cau hinh') {
    return {
      src: '/assets/brand/dntech-build-pc-hero-v3.png',
      alt: 'Linh kiện dành cho công cụ xây dựng cấu hình PC',
      caption: 'PC Configurator',
    };
  }

  if (label === 'lich ve sinh may') {
    return {
      src: '/assets/brand/dntech-performance-hero.png',
      alt: 'Dịch vụ vệ sinh và kiểm tra hệ thống PC',
      caption: 'Performance Care',
    };
  }

  return null;
}

function isQuickChatLink(label?: string, href?: string) {
  const normalizedLabel = normalizeCatalogText(label || '');
  const normalizedHref = String(href || '').trim();
  return (
    normalizedHref === '#chat-quick' ||
    normalizedLabel === 'lien he ho tro' ||
    normalizedLabel === 'lien he bao gia' ||
    normalizedLabel === 'ho tro ky thuat'
  );
}

export default function Header() {
  const siteConfig = useSiteConfig();
  const initialNavigation = siteConfig?.navigation;
  const formatPrice = (value: number) => `${new Intl.NumberFormat('vi-VN').format(value)}₫`;
  const router = useRouter();
  const pathname = usePathname();
  const [mounted, setMounted] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeMegaIndex, setActiveMegaIndex] = useState(0);
  const [headerHeight, setHeaderHeight] = useState(92);
  const [headerScrolled, setHeaderScrolled] = useState(false);
  const [mobileSearchHidden, setMobileSearchHidden] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchSuggestItems, setSearchSuggestItems] = useState<SearchSuggestItem[]>([]);
  const [searchSuggestLoading, setSearchSuggestLoading] = useState(false);
  const [searchSuggestOpen, setSearchSuggestOpen] = useState(false);
  const [voiceListening, setVoiceListening] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(true);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [cartMenuOpen, setCartMenuOpen] = useState(false);
  const [topNavDropdownOpen, setTopNavDropdownOpen] = useState<string | null>(null);
  const [quickSupportEnabled, setQuickSupportEnabled] = useState(true);
  const [navigationReady, setNavigationReady] = useState(Boolean(initialNavigation));
  const [navItems, setNavItems] = useState<TopNavItem[]>(
    initialNavigation?.topNav || defaultMenuConfig.topNav
  );
  const [megaMenuCategories, setMegaMenuCategories] = useState<MegaMenuItem[]>(
    initialNavigation?.megaMenu || []
  );
  const [quickSearchLinks, setQuickSearchLinks] = useState<string[]>(
    initialNavigation?.quickSearch || defaultMenuConfig.quickSearch
  );
  const [displayTextConfig, setDisplayTextConfig] = useState<DisplayTextConfig>(
    initialNavigation?.displayTextConfig || defaultDisplayTextConfig
  );
  const [shopLogoUrl, setShopLogoUrl] = useState(initialNavigation?.shopInfoConfig?.logoUrl || '');
  const [shopName, setShopName] = useState(initialNavigation?.shopInfoConfig?.name || 'DN TECH');
  const { items, totalItems, totalPrice, removeFromCart } = useCart();
  const { user, signOut, loading: authLoading } = useAuth();
  const userMenuRef = useRef<HTMLDivElement>(null);
  const cartMenuRef = useRef<HTMLDivElement>(null);
  const searchBoxRef = useRef<HTMLDivElement>(null);
  const speechRecognitionRef = useRef<DnTechSpeechRecognition | null>(null);
  const headerRef = useRef<HTMLElement>(null);
  const mobileSearchHiddenRef = useRef(false);
  const categoryTriggerRef = useRef<HTMLDivElement>(null);
  const megaMenuRef = useRef<HTMLDivElement>(null);
  const closeUserMenuTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isHoveringUserTriggerRef = useRef(false);
  const isHoveringUserMenuRef = useRef(false);
  const closeMenuTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isHoveringTriggerRef = useRef(false);
  const isHoveringMenuRef = useRef(false);
  const closeCartMenuTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isHoveringCartTriggerRef = useRef(false);
  const isHoveringCartMenuRef = useRef(false);
  const closeTopNavDropdownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  mobileSearchHiddenRef.current = mobileSearchHidden;

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Recognition) {
      setVoiceSupported(false);
      return;
    }

    const recognition = new Recognition();
    recognition.lang = 'vi-VN';
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.maxAlternatives = 3;
    recognition.onstart = () => setVoiceListening(true);
    recognition.onend = () => setVoiceListening(false);
    recognition.onerror = () => setVoiceListening(false);
    recognition.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map((result) => result[0]?.transcript || '')
        .join(' ')
        .trim();
      const normalized = normalizeVoiceSearchQuery(transcript);
      const nextQuery = normalized || transcript;
      const hasFinalResult = Array.from(event.results).some((result) => result.isFinal);

      setSearchQuery(nextQuery);
      setSearchSuggestOpen(false);

      if (hasFinalResult && nextQuery) {
        router.push(`/tim-kiem?q=${encodeURIComponent(nextQuery)}`);
      }
    };

    speechRecognitionRef.current = recognition;
    return () => {
      recognition.stop();
      speechRecognitionRef.current = null;
    };
  }, [router]);

  useEffect(() => {
    fetch('/api/settings/public', { cache: 'no-store' })
      .then((res) => (res.ok ? res.json() : null))
      .then((payload) => {
        setQuickSupportEnabled(payload?.settings?.support_config?.quickSupportEnabled !== false);
      })
      .catch(() => setQuickSupportEnabled(true));
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
      if (cartMenuRef.current && !cartMenuRef.current.contains(e.target as Node)) {
        setCartMenuOpen(false);
      }
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target as Node)) {
        setSearchSuggestOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2) {
      setSearchSuggestItems([]);
      setSearchSuggestLoading(false);
      setSearchSuggestOpen(false);
      return;
    }
    setSearchSuggestLoading(true);
    const t = window.setTimeout(() => {
      fetch(`/api/products?q=${encodeURIComponent(q)}&limit=8`)
        .then((res) => res.json())
        .then((data: { items?: SearchSuggestItem[] }) => {
          const raw = data.items || [];
          setSearchSuggestItems(
            raw.map((it) => ({
              id: String(it.id),
              name: it.name,
              slug: it.slug,
              image: it.image ?? null,
              price: Number(it.price ?? 0),
            }))
          );
        })
        .catch(() => {
          setSearchSuggestItems([]);
        })
        .finally(() => {
          setSearchSuggestLoading(false);
          setSearchSuggestOpen(true);
        });
    }, 280);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSearchSuggestOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (initialNavigation) {
      setNavItems(initialNavigation.topNav || defaultMenuConfig.topNav);
      setMegaMenuCategories(initialNavigation.megaMenu || []);
      setQuickSearchLinks(initialNavigation.quickSearch || defaultMenuConfig.quickSearch);
      setDisplayTextConfig(initialNavigation.displayTextConfig || defaultDisplayTextConfig);
      setShopLogoUrl(initialNavigation.shopInfoConfig?.logoUrl || '');
      setShopName(initialNavigation.shopInfoConfig?.name || 'DN TECH');
      setNavigationReady(true);
      return;
    }

    fetch('/api/navigation')
      .then((res) => res.json())
      .then((payload) => {
        setNavItems(payload.topNav || defaultMenuConfig.topNav);
        setMegaMenuCategories(payload.megaMenu || []);
        setQuickSearchLinks(payload.quickSearch || defaultMenuConfig.quickSearch);
        setDisplayTextConfig(payload.displayTextConfig || defaultDisplayTextConfig);
        setShopLogoUrl(payload.shopInfoConfig?.logoUrl || '');
        setShopName(payload.shopInfoConfig?.name || 'DN TECH');
      })
      .catch(() => {
        setNavItems(defaultMenuConfig.topNav);
        setQuickSearchLinks(defaultMenuConfig.quickSearch);
        setDisplayTextConfig(defaultDisplayTextConfig);
        setShopLogoUrl('');
        setShopName('DN TECH');
      })
      .finally(() => {
        setNavigationReady(true);
      });
  }, [initialNavigation]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const q = new URLSearchParams(window.location.search).get('q') || '';
    setSearchQuery(q.trim());
  }, [pathname]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const phoneQuery = window.matchMedia('(max-width: 767px)');
    let rafId: number | null = null;
    let lastScrollY = Math.max(0, window.scrollY);
    let direction: -1 | 0 | 1 = 0;
    let directionalDistance = 0;
    let searchHidden = false;

    const applyMobileSearchHidden = (nextHidden: boolean) => {
      if (searchHidden === nextHidden) return;
      searchHidden = nextHidden;
      setMobileSearchHidden(nextHidden);
      if (nextHidden) setSearchSuggestOpen(false);
    };

    const onScroll = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = null;
        const currentScrollY = Math.max(0, window.scrollY);
        const delta = currentScrollY - lastScrollY;
        lastScrollY = currentScrollY;
        const isScrolled = currentScrollY > 10;
        setHeaderScrolled((prev) => (prev !== isScrolled ? isScrolled : prev));

        const searchIsActive = Boolean(searchBoxRef.current?.contains(document.activeElement));

        if (!phoneQuery.matches || currentScrollY <= 24 || searchIsActive) {
          direction = 0;
          directionalDistance = 0;
          applyMobileSearchHidden(false);
          return;
        }

        if (Math.abs(delta) < 1) return;

        const nextDirection: -1 | 1 = delta > 0 ? 1 : -1;
        if (nextDirection !== direction) {
          direction = nextDirection;
          directionalDistance = 0;
        }
        directionalDistance += Math.abs(delta);

        if (direction === 1 && currentScrollY > 96 && directionalDistance >= 16) {
          directionalDistance = 0;
          applyMobileSearchHidden(true);
        } else if (direction === -1 && directionalDistance >= 10) {
          directionalDistance = 0;
          applyMobileSearchHidden(false);
        }
      });
    };

    const onViewportChange = () => {
      lastScrollY = Math.max(0, window.scrollY);
      direction = 0;
      directionalDistance = 0;
      applyMobileSearchHidden(false);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    phoneQuery.addEventListener('change', onViewportChange);
    return () => {
      window.removeEventListener('scroll', onScroll);
      phoneQuery.removeEventListener('change', onViewportChange);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [pathname]);

  useEffect(() => {
    setMobileSearchHidden(false);
  }, [pathname]);

  useEffect(() => {
    return () => {
      if (closeMenuTimeoutRef.current) {
        clearTimeout(closeMenuTimeoutRef.current);
        closeMenuTimeoutRef.current = null;
      }
      if (closeUserMenuTimeoutRef.current) {
        clearTimeout(closeUserMenuTimeoutRef.current);
        closeUserMenuTimeoutRef.current = null;
      }
      if (closeCartMenuTimeoutRef.current) {
        clearTimeout(closeCartMenuTimeoutRef.current);
        closeCartMenuTimeoutRef.current = null;
      }
      if (closeTopNavDropdownTimeoutRef.current) {
        clearTimeout(closeTopNavDropdownTimeoutRef.current);
        closeTopNavDropdownTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const measureHeader = (force = false) => {
      const isPhone = typeof window !== 'undefined' && window.innerWidth < 768;
      const searchRail = headerRef.current?.querySelector('.header-mobile-search');
      const searchRailIsAnimating = Boolean(
        searchRail &&
        'getAnimations' in searchRail &&
        searchRail.getAnimations().some((animation) => animation.playState === 'running')
      );

      if (!force && isPhone && (mobileSearchHiddenRef.current || searchRailIsAnimating)) {
        return;
      }

      const h = headerRef.current?.offsetHeight || 0;
      if (h > 0) setHeaderHeight(h);
    };

    measureHeader(true);
    const resizeObserver =
      typeof window !== 'undefined' && 'ResizeObserver' in window
        ? new ResizeObserver(() => measureHeader())
        : null;
    if (resizeObserver && headerRef.current) {
      resizeObserver.observe(headerRef.current);
    }

    return () => {
      resizeObserver?.disconnect();
    };
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const effectiveHeaderHeight = mobileSearchHidden ? 72 : headerHeight;
    document.documentElement.style.setProperty(
      '--site-header-height',
      `${effectiveHeaderHeight}px`
    );
  }, [headerHeight, mobileSearchHidden]);

  const clearCloseMenuTimer = () => {
    if (closeMenuTimeoutRef.current) {
      clearTimeout(closeMenuTimeoutRef.current);
      closeMenuTimeoutRef.current = null;
    }
  };

  const scheduleCloseMenuFromTrigger = () => {
    clearCloseMenuTimer();
    closeMenuTimeoutRef.current = setTimeout(() => {
      closeMenuTimeoutRef.current = null;
      if (!isHoveringMenuRef.current && !isHoveringTriggerRef.current) {
        setMenuOpen(false);
      }
    }, 280);
  };

  const clearCloseUserMenuTimer = () => {
    if (closeUserMenuTimeoutRef.current) {
      clearTimeout(closeUserMenuTimeoutRef.current);
      closeUserMenuTimeoutRef.current = null;
    }
  };

  const scheduleCloseUserMenuFromTrigger = () => {
    clearCloseUserMenuTimer();
    closeUserMenuTimeoutRef.current = setTimeout(() => {
      closeUserMenuTimeoutRef.current = null;
      if (!isHoveringUserMenuRef.current) {
        setUserMenuOpen(false);
      }
    }, 200);
  };

  const clearCloseCartMenuTimer = () => {
    if (closeCartMenuTimeoutRef.current) {
      clearTimeout(closeCartMenuTimeoutRef.current);
      closeCartMenuTimeoutRef.current = null;
    }
  };

  const scheduleCloseCartMenuFromTrigger = () => {
    clearCloseCartMenuTimer();
    closeCartMenuTimeoutRef.current = setTimeout(() => {
      closeCartMenuTimeoutRef.current = null;
      if (!isHoveringCartMenuRef.current && !isHoveringCartTriggerRef.current) {
        setCartMenuOpen(false);
      }
    }, 220);
  };

  const clearTopNavDropdownTimer = () => {
    if (closeTopNavDropdownTimeoutRef.current) {
      clearTimeout(closeTopNavDropdownTimeoutRef.current);
      closeTopNavDropdownTimeoutRef.current = null;
    }
  };

  const scheduleCloseTopNavDropdown = () => {
    clearTopNavDropdownTimer();
    closeTopNavDropdownTimeoutRef.current = setTimeout(() => {
      closeTopNavDropdownTimeoutRef.current = null;
      setTopNavDropdownOpen(null);
    }, 180);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } finally {
      setUserMenuOpen(false);
    }
  };

  const handleQuickChatLinkClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    label?: string,
    href?: string
  ) => {
    if (!isQuickChatLink(label, href)) return;
    event.preventDefault();
    setTopNavDropdownOpen(null);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('dntech:open-chat'));
    }
  };

  const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const q = normalizeVoiceSearchQuery(searchQuery) || searchQuery.trim();
    if (!q) return;
    setSearchSuggestOpen(false);
    router.push(`/tim-kiem?q=${encodeURIComponent(q)}`);
  };

  const handleVoiceSearchClick = () => {
    const recognition = speechRecognitionRef.current;
    if (!recognition) return;

    if (voiceListening) {
      recognition.stop();
      return;
    }

    setSearchSuggestOpen(false);
    recognition.start();
  };

  const handleLogoClick = () => {
    setMenuOpen(false);
    setUserMenuOpen(false);
  };

  const resolvedMegaCategories = useMemo(() => {
    // The public storefront uses the same seven grouped entries as dntechstore.com.
    // Keep the database's individual categories for product filtering, but present
    // the compact grouped menu in the header.
    const groupedTemplate = templateMegaMenuCategories.filter((_, index) =>
      [2, 3, 4, 5, 6, 7, 11].includes(index)
    );
    if (groupedTemplate.length) return groupedTemplate;

    if (megaMenuCategories.length) {
      const fallbackIconMap = new Map(
        templateMegaMenuCategories.map((item) => [normalizeCatalogText(item.label), item.icon])
      );
      return megaMenuCategories.map((item) => {
        if (item.icon) return item;
        const fallback = fallbackIconMap.get(normalizeCatalogText(item.label || '')) || '';
        return { ...item, icon: fallback };
      });
    }

    if (templateMegaMenuCategories.length) {
      return templateMegaMenuCategories;
    }

    return megaPresetCategories.map((preset) => {
      const matched = megaMenuCategories.find((item) => {
        const normalizedLabel = normalizeCatalogText(item.label || '');
        return preset.keywords.some((keyword) =>
          normalizedLabel.includes(normalizeCatalogText(keyword))
        );
      });

      return {
        label: preset.label,
        icon: preset.icon,
        href: matched?.href || `/tim-kiem?q=${encodeURIComponent(preset.label)}`,
        sub: matched?.sub || [],
        is_featured: matched?.is_featured,
      } as MegaMenuItem;
    });
  }, [megaMenuCategories]);

  useEffect(() => {
    if (!menuOpen) return;
    setActiveMegaIndex(0);
  }, [menuOpen]);

  const logoUrl = displayTextConfig.brandImageUrl || shopLogoUrl;
  const configuredLogoHeight = Number(displayTextConfig.brandLogoHeight) || 56;
  const logoHeight = Math.min(96, Math.max(48, Math.round(configuredLogoHeight)));
  const logoFrameHeight = Math.min(78, Math.max(56, Math.round(logoHeight * 0.92)));
  const logoFrameWidth = Math.min(180, Math.max(120, Math.round(logoHeight * 1.8)));
  const mobileLogoHeight = Math.min(64, Math.max(48, Math.round(configuredLogoHeight * 0.68)));
  const mobileLogoFrameHeight = Math.max(56, mobileLogoHeight);
  const mobileLogoFrameWidth = Math.min(124, Math.max(92, Math.round(mobileLogoHeight * 1.65)));
  const logoSizingVars = {
    '--header-logo-height': `${logoHeight}px`,
    '--header-logo-frame-height': `${logoFrameHeight}px`,
    '--header-logo-frame-width': `${logoFrameWidth}px`,
    '--header-logo-mobile-height': `${mobileLogoHeight}px`,
    '--header-logo-mobile-frame-height': `${mobileLogoFrameHeight}px`,
    '--header-logo-mobile-frame-width': `${mobileLogoFrameWidth}px`,
  } as React.CSSProperties;

  return (
    <>
      <header
        id="insHeaderPage"
        ref={headerRef}
        data-scrolled={headerScrolled ? 'true' : 'false'}
        data-mobile-search-hidden={mobileSearchHidden ? 'true' : 'false'}
        className={`site-header site-header--motion fixed inset-x-0 top-0 z-[1000] shadow-sm ${
          headerScrolled ? 'site-header--scrolled' : ''
        }`}
      >
        {/* Top nav bar */}
        <div className="header-top-bar relative hidden overflow-visible border-b border-white/25 shadow-[0_2px_12px_rgba(120,80,0,0.2)] xl:block">
          <div
            className="max-w-[1500px] mx-auto px-4 relative z-[1]"
            style={{ visibility: navigationReady ? 'visible' : 'hidden' }}
          >
            <ul className="flex min-h-[44px] items-center gap-0 overflow-visible xl:flex-wrap">
              {navItems
                ?.filter(
                  (item) =>
                    normalizeCatalogText(item?.label || '') !== 'hotline' &&
                    (quickSupportEnabled || !isQuickChatLink(item?.label, item?.href))
                )
                .map((item, index) => {
                  const dropdownItems = getTopNavDropdownItems(item).filter(
                    (dropdownItem) =>
                      quickSupportEnabled || !isQuickChatLink(dropdownItem.label, dropdownItem.href)
                  );
                  const dropdownKey = `${normalizeCatalogText(item?.label || '')}-${index}`;
                  const hasDropdown = dropdownItems.length > 0;
                  const dropdownVisual = getTopNavDropdownVisual(item);
                  const alignDropdownRight = index >= Math.max(0, (navItems?.length || 0) - 2);

                  return (
                    <li
                      key={`${item?.label || 'nav'}-${index}`}
                      className="relative"
                      onMouseEnter={() => {
                        if (!hasDropdown) return;
                        clearTopNavDropdownTimer();
                        setTopNavDropdownOpen(dropdownKey);
                      }}
                      onMouseLeave={() => {
                        if (!hasDropdown) return;
                        scheduleCloseTopNavDropdown();
                      }}
                    >
                      <Link
                        href={item?.href}
                        className="header-top-link relative flex items-center gap-1.5 px-3.5 py-2.5 text-white text-[15px] font-semibold whitespace-nowrap rounded-lg transition-colors duration-200 hover:bg-white/15"
                      >
                        {item?.icon ? (
                          <img
                            src={item?.icon}
                            alt={item?.label}
                            width={16}
                            height={16}
                            className="w-4 h-4 object-contain"
                          />
                        ) : null}
                        <span>{item?.label}</span>
                        {hasDropdown ? (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="10"
                            height="10"
                            fill="currentColor"
                            viewBox="0 0 16 16"
                            className={`mt-px transition-transform duration-200 ${
                              topNavDropdownOpen === dropdownKey ? 'rotate-180' : ''
                            }`}
                            aria-hidden
                          >
                            <path d="M1.5 5.5 8 12l6.5-6.5-1-1L8 10 2.5 4.5z" />
                          </svg>
                        ) : null}
                      </Link>

                      {hasDropdown && topNavDropdownOpen === dropdownKey ? (
                        <div
                          className={`header-hover-surface header-hover-surface--compact absolute top-full z-[1200] mt-2 w-[360px] max-w-[calc(100vw-24px)] overflow-hidden rounded-2xl border border-neutral-200 bg-white p-2.5 shadow-[0_24px_54px_rgba(0,0,0,0.24)] ${
                            alignDropdownRight ? 'right-0' : 'left-0'
                          }`}
                          onMouseEnter={clearTopNavDropdownTimer}
                          onMouseLeave={scheduleCloseTopNavDropdown}
                        >
                          {dropdownVisual ? (
                            <div className="relative h-[118px] overflow-hidden rounded-xl bg-[#0b0b0b]">
                              <Image
                                src={dropdownVisual.src}
                                alt={dropdownVisual.alt}
                                fill
                                quality={84}
                                sizes="360px"
                                className="h-full w-full object-cover object-right"
                              />
                              <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(8,8,8,.94)_0%,rgba(8,8,8,.55)_52%,rgba(8,8,8,.08)_100%)]" />
                              <div className="absolute inset-y-0 left-0 w-1 bg-[#ffd400]" />
                              <div className="absolute inset-0 flex flex-col justify-end p-4">
                                <span className="text-[9px] font-black uppercase tracking-[0.18em] text-[#ffd400]">
                                  DN TECH
                                </span>
                                <span className="mt-1 max-w-[62%] text-base font-black leading-tight text-white">
                                  {dropdownVisual.caption}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <div className="rounded-xl bg-gradient-to-r from-amber-50 to-white px-3 py-2.5 text-xs font-bold uppercase tracking-[0.08em] text-amber-700">
                              {item.label}
                            </div>
                          )}
                          <div className="mt-2 space-y-1.5">
                            {dropdownItems.map((dropdownItem) => (
                              <Link
                                key={`${dropdownKey}-${dropdownItem.href}-${dropdownItem.label}`}
                                href={dropdownItem.href}
                                onClick={(event) => {
                                  handleQuickChatLinkClick(
                                    event,
                                    dropdownItem.label,
                                    dropdownItem.href
                                  );
                                  if (!event.defaultPrevented) {
                                    setTopNavDropdownOpen(null);
                                  }
                                }}
                                className="block rounded-xl border border-transparent px-3 py-2.5 transition-colors hover:border-amber-200 hover:bg-amber-50/70"
                              >
                                <div className="text-sm font-semibold text-slate-800">
                                  {dropdownItem.label}
                                </div>
                                {dropdownItem.description ? (
                                  <div className="mt-0.5 text-xs leading-5 text-slate-500">
                                    {dropdownItem.description}
                                  </div>
                                ) : null}
                              </Link>
                            ))}
                          </div>
                        </div>
                      ) : null}
                    </li>
                  );
                })}
            </ul>
          </div>
        </div>

        {/* Main header */}
        <div className="header-main-bar glass-panel border-b border-amber-200 bg-white">
          <div className="max-w-[1500px] mx-auto px-4">
            <div
              className={`header-main-row flex flex-wrap items-center gap-2 py-1 lg:flex-nowrap lg:py-1.5 ${
                mobileSearchHidden ? 'header-main-row--mobile-search-hidden' : ''
              }`}
            >
              {/* Logo */}
              <div className="flex-shrink-0">
                <Link
                  href="/"
                  className="group block cursor-pointer"
                  onClick={handleLogoClick}
                  aria-label="Về trang chủ"
                >
                  <div
                    className="header-logo-frame relative flex items-center justify-center overflow-visible transition-transform duration-300 ease-out group-hover:scale-[1.04] group-active:scale-[0.98]"
                    style={logoSizingVars}
                  >
                    {logoUrl ? (
                      <img
                        src={logoUrl}
                        alt={shopName}
                        width={logoFrameWidth}
                        height={logoHeight}
                        decoding="async"
                        fetchPriority="high"
                        className="header-logo-image block h-auto w-auto object-contain"
                      />
                    ) : (
                      <div className="header-logo-placeholder rounded-xl border border-slate-200 bg-white/80" />
                    )}
                  </div>
                </Link>
              </div>

              {/* Category button — shrink-0 + nowrap so label never stacks */}
              <div
                className={`header-category-trigger relative hidden shrink-0 items-center lg:flex ${
                  menuOpen ? 'header-category-trigger--open' : ''
                }`}
                ref={categoryTriggerRef}
                onMouseEnter={() => {
                  isHoveringTriggerRef.current = true;
                  clearCloseMenuTimer();
                  setMenuOpen(true);
                }}
                onMouseLeave={() => {
                  isHoveringTriggerRef.current = false;
                  scheduleCloseMenuFromTrigger();
                }}
              >
                <button
                  type="button"
                  title="Danh mục sản phẩm"
                  onClick={() => setMenuOpen(!menuOpen)}
                  aria-expanded={menuOpen}
                  className={`header-category-btn inline-flex shrink-0 flex-nowrap items-center gap-2.5 rounded-xl border border-slate-200/80 bg-white/90 px-4 py-2 text-[15px] font-semibold leading-none text-gray-700 shadow-sm transition-[background-color,border-color,box-shadow,color] duration-200 hover:border-primary/40 hover:bg-white hover:text-primary lg:py-2.5 ${
                    menuOpen ? 'border-primary/40 bg-white text-primary' : ''
                  }`}
                >
                  <svg
                    className="shrink-0"
                    fill="currentColor"
                    viewBox="0 0 512 512"
                    width="18"
                    height="18"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <line
                      x1="88"
                      y1="152"
                      x2="424"
                      y2="152"
                      fill="none"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeMiterlimit="10"
                      strokeWidth="48px"
                    />
                    <line
                      x1="88"
                      y1="256"
                      x2="424"
                      y2="256"
                      fill="none"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeMiterlimit="10"
                      strokeWidth="48px"
                    />
                    <line
                      x1="88"
                      y1="360"
                      x2="424"
                      y2="360"
                      fill="none"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeMiterlimit="10"
                      strokeWidth="48px"
                    />
                  </svg>
                  <span className="whitespace-nowrap">
                    <span className="xl:hidden">Danh mục</span>
                    <span className="hidden xl:inline">Danh mục sản phẩm</span>
                  </span>
                </button>
              </div>

              {/* Search */}
              <div
                className={`header-mobile-search mobile-search-rail order-3 w-full basis-full lg:order-none lg:min-w-0 lg:flex-1 lg:basis-auto ${
                  mobileSearchHidden ? 'mobile-search-rail--hidden' : ''
                }`}
                aria-hidden={mobileSearchHidden || undefined}
              >
                <div className="relative" ref={searchBoxRef}>
                  <form className="flex" onSubmit={handleSearchSubmit}>
                    <div className="header-search-field flex min-w-0 flex-1 items-center rounded-xl border border-slate-200 bg-white shadow-sm">
                      <input
                        type="text"
                        placeholder="Tìm kiếm sản phẩm…"
                        value={searchQuery}
                        aria-label="Tìm kiếm sản phẩm"
                        autoComplete="off"
                        onChange={(e) => setSearchQuery(e?.target?.value)}
                        onFocus={() => {
                          setMobileSearchHidden(false);
                          if (searchQuery.trim().length >= 2 && searchSuggestItems.length > 0) {
                            setSearchSuggestOpen(true);
                          }
                        }}
                        className="min-w-0 flex-1 rounded-none border-0 bg-transparent px-3 py-2 text-sm text-slate-700 outline-none md:px-4 md:text-base"
                      />

                      <button
                        type="button"
                        onClick={handleVoiceSearchClick}
                        disabled={!voiceSupported}
                        className={`flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-full bg-transparent text-slate-700 transition-colors hover:text-primary disabled:cursor-not-allowed disabled:text-slate-400 md:h-[44px] md:w-[44px] ${
                          voiceListening ? 'bg-red-50 text-red-600' : ''
                        }`}
                        aria-label={
                          voiceListening
                            ? 'Dừng tìm kiếm bằng giọng nói'
                            : 'Tìm kiếm bằng giọng nói'
                        }
                        title={
                          voiceSupported
                            ? 'Tìm kiếm bằng giọng nói'
                            : 'Trình duyệt chưa hỗ trợ tìm kiếm giọng nói'
                        }
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth="2"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 3a3 3 0 0 0-3 3v6a3 3 0 1 0 6 0V6a3 3 0 0 0-3-3Z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M19 11a7 7 0 0 1-14 0M12 18v3M8 21h8"
                          />
                        </svg>
                      </button>
                    </div>

                    <button
                      type="submit"
                      className="header-search-submit btn-primary-modern ml-2 flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full border-0 px-3.5 py-2 text-sm font-semibold shadow-none transition-colors md:px-5 md:text-base"
                      aria-label="Tìm kiếm"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        viewBox="0 0 16 16"
                      >
                        <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.099zm-5.242 1.656a5.5 5.5 0 1 1 0-11 5.5 5.5 0 0 1 0 11z" />
                      </svg>
                      <span className="hidden md:inline">Tìm kiếm</span>
                    </button>
                  </form>
                  {searchSuggestOpen && searchQuery.trim().length >= 2 ? (
                    <div
                      className="absolute left-0 right-0 top-full z-[15000] mt-2.5 max-h-[min(70vh,28rem)] overflow-y-auto rounded-2xl border-2 border-[#eab308] bg-white py-2 shadow-[0_24px_60px_rgba(0,0,0,0.25)] ring-4 ring-[#eab308]/20"
                      role="listbox"
                      aria-label="Gợi ý sản phẩm"
                    >
                      {searchSuggestLoading ? (
                        <div className="px-5 py-4 text-[15px] font-medium text-slate-500">
                          Đang tìm kiếm...
                        </div>
                      ) : searchSuggestItems.length === 0 ? (
                        <div className="px-5 py-4 text-[15px] font-medium text-slate-500">
                          Không tìm thấy sản phẩm phù hợp.
                        </div>
                      ) : (
                        searchSuggestItems.map((item) => (
                          <Link
                            key={item.id}
                            href={`/san-pham/${item.slug}`}
                            role="option"
                            className="group flex items-center gap-4 px-4 py-3 transition-colors duration-150 hover:bg-[#fffbeb] border-b border-gray-50 last:border-0"
                            onClick={() => setSearchSuggestOpen(false)}
                          >
                            <span className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white border border-gray-100 shadow-sm transition-[border-color] duration-150 group-hover:border-[#eab308]">
                              {toOptionalUrl(item.image) ? (
                                <img
                                  src={toOptionalUrl(item.image)!}
                                  alt=""
                                  className="h-full w-full object-contain p-1.5 mix-blend-multiply transition-transform group-hover:scale-110"
                                />
                              ) : (
                                <span className="text-xs text-slate-400">—</span>
                              )}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="line-clamp-2 text-[15px] font-bold text-slate-800 transition-colors group-hover:text-[#d4a100]">
                                {item.name}
                              </span>
                              <span className="text-[14px] font-extrabold text-red-600 mt-0.5 block">
                                {new Intl.NumberFormat('vi-VN').format(item.price)}₫
                              </span>
                            </span>
                          </Link>
                        ))
                      )}
                    </div>
                  ) : null}
                  <div className="mt-1.5 hidden max-w-full flex-nowrap gap-2 overflow-x-auto overscroll-x-contain pb-0.5 [scrollbar-width:thin] lg:flex">
                    {quickSearchLinks?.map((link) => (
                      <Link
                        key={link}
                        href={`/tim-kiem?q=${encodeURIComponent(link)}`}
                        className="shrink-0 whitespace-nowrap rounded-none bg-transparent px-3.5 py-1.5 text-[13px] font-medium text-slate-700 transition-colors hover:text-primary"
                      >
                        {link}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              {/* Account & Cart — shrink-0 keeps labels on one line; pills align in the header rail */}
              <div className="order-2 ml-auto flex shrink-0 flex-nowrap items-center gap-1.5 lg:order-none lg:ml-0 lg:gap-2">
                <button
                  type="button"
                  onClick={() => setMenuOpen((prev) => !prev)}
                  aria-label="Mở danh mục sản phẩm"
                  aria-expanded={menuOpen}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 shadow-sm transition-colors hover:text-primary lg:hidden"
                >
                  <svg
                    fill="none"
                    viewBox="0 0 24 24"
                    width="18"
                    height="18"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <line
                      x1="4"
                      y1="6"
                      x2="20"
                      y2="6"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                    />
                    <line
                      x1="4"
                      y1="12"
                      x2="20"
                      y2="12"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                    />
                    <line
                      x1="4"
                      y1="18"
                      x2="20"
                      y2="18"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>

                {/* Account button */}
                <div
                  className="relative"
                  ref={userMenuRef}
                  onMouseEnter={() => {
                    if (!user) return;
                    isHoveringUserTriggerRef.current = true;
                    clearCloseUserMenuTimer();
                    setUserMenuOpen(true);
                  }}
                  onMouseLeave={() => {
                    if (!user) return;
                    isHoveringUserTriggerRef.current = false;
                    scheduleCloseUserMenuFromTrigger();
                  }}
                >
                  {!authLoading && user ? (
                    <button
                      onClick={() => setUserMenuOpen(!userMenuOpen)}
                      type="button"
                      className="header-icon-link header-action-pill inline-flex h-11 w-11 shrink-0 flex-col items-center justify-center rounded-xl bg-transparent p-0 text-gray-700 lg:h-auto lg:w-auto lg:min-w-0 lg:flex-row lg:justify-start lg:gap-1.5 lg:px-2.5 lg:py-1.5"
                      aria-expanded={userMenuOpen}
                    >
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-100/90 text-amber-700 ring-1 ring-amber-200/50">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                          aria-hidden
                        >
                          <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4zm-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.029 10 8 10c-2.029 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664h10z" />
                        </svg>
                      </span>
                      <span className="hidden whitespace-nowrap text-left text-[13px] font-semibold leading-tight text-slate-800 lg:inline">
                        Tài khoản
                      </span>
                    </button>
                  ) : (
                    <Link
                      href="/dang-nhap"
                      className="header-icon-link header-action-pill inline-flex h-11 w-11 shrink-0 flex-col items-center justify-center rounded-xl bg-transparent p-0 text-gray-700 lg:h-auto lg:w-auto lg:min-w-0 lg:flex-row lg:justify-start lg:gap-1.5 lg:px-2.5 lg:py-1.5"
                    >
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-100/90 text-amber-700 ring-1 ring-amber-200/50">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                          aria-hidden
                        >
                          <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4zm-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.029 10 8 10c-2.029 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664h10z" />
                        </svg>
                      </span>
                      <span className="hidden whitespace-nowrap text-left text-[13px] font-semibold leading-tight text-slate-800 lg:inline">
                        Tài khoản
                      </span>
                    </Link>
                  )}

                  {/* User dropdown */}
                  {userMenuOpen && user && (
                    <div
                      role="menu"
                      className="header-user-dropdown header-hover-surface header-hover-surface--compact absolute right-0 top-full z-[1100] mt-1.5 w-56 rounded-xl py-1.5"
                      onMouseEnter={() => {
                        isHoveringUserMenuRef.current = true;
                        clearCloseUserMenuTimer();
                        setUserMenuOpen(true);
                      }}
                      onMouseLeave={() => {
                        isHoveringUserMenuRef.current = false;
                        setUserMenuOpen(false);
                        clearCloseUserMenuTimer();
                      }}
                    >
                      <div className="border-b border-slate-200/90 bg-slate-50/80 px-3.5 py-2.5">
                        <p className="truncate text-xs font-bold text-slate-900">
                          {user?.full_name || 'Tài khoản'}
                        </p>
                        <p className="mt-0.5 truncate text-[11px] text-slate-600">{user?.email}</p>
                      </div>
                      <Link
                        href="/account"
                        role="menuitem"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 px-3.5 py-2 text-sm font-medium text-slate-800 transition-colors hover:bg-amber-50/90 hover:text-amber-800"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                        >
                          <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
                          <path
                            fillRule="evenodd"
                            d="M0 14s1-1 4-1 4 1 4 1 1-1 4-1 4 1 4 1v1H0v-1z"
                          />
                        </svg>
                        Thông tin tài khoản
                      </Link>
                      <Link
                        href="/account"
                        role="menuitem"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 px-3.5 py-2 text-sm font-medium text-slate-800 transition-colors hover:bg-amber-50/90 hover:text-amber-800"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                        >
                          <path d="M0 1.5A.5.5 0 0 1 .5 1H2a.5.5 0 0 1 .485.379L2.89 3H14.5a.5.5 0 0 1 .491.592l-1.5 8A.5.5 0 0 1 13 12H4a.5.5 0 0 1-.491-.408L2.01 3.607 1.61 2H.5a.5.5 0 0 1-.5-.5z" />
                        </svg>
                        Lịch sử đơn hàng
                      </Link>
                      <div className="mt-1 border-t border-slate-200/90 pt-1">
                        <button
                          type="button"
                          role="menuitem"
                          onClick={handleSignOut}
                          className="flex w-full items-center gap-2 px-3.5 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            fill="currentColor"
                            viewBox="0 0 16 16"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 12.5a.5.5 0 0 1-.5.5h-8a.5.5 0 0 1-.5-.5v-9a.5.5 0 0 1 .5-.5h8a.5.5 0 0 1 .5.5v2a.5.5 0 0 0 1 0v-2A1.5 1.5 0 0 0 9.5 2h-8A1.5 1.5 0 0 0 0 3.5v9A1.5 1.5 0 0 0 1.5 14h8a1.5 1.5 0 0 0 1.5-1.5v-2a.5.5 0 0 0-1 0v2z"
                            />
                            <path
                              fillRule="evenodd"
                              d="M15.854 8.354a.5.5 0 0 0 0-.708l-3-3a.5.5 0 0 0-.708.708L14.293 7.5H5.5a.5.5 0 0 0 0 1h8.793l-2.147 2.146a.5.5 0 0 0 .708.708l3-3z"
                            />
                          </svg>
                          Đăng xuất
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div
                  className="relative"
                  ref={cartMenuRef}
                  onMouseEnter={() => {
                    isHoveringCartTriggerRef.current = true;
                    clearCloseCartMenuTimer();
                    setCartMenuOpen(true);
                  }}
                  onMouseLeave={() => {
                    isHoveringCartTriggerRef.current = false;
                    scheduleCloseCartMenuFromTrigger();
                  }}
                >
                  <Link
                    href="/gio-hang"
                    aria-label={totalItems ? `Giỏ hàng, ${totalItems} sản phẩm` : 'Giỏ hàng'}
                    className="header-icon-link header-action-pill inline-flex h-11 w-11 shrink-0 flex-col items-center justify-center rounded-xl bg-transparent p-0 text-slate-800 lg:h-auto lg:w-auto lg:min-w-0 lg:flex-row lg:justify-start lg:gap-1.5 lg:px-2.5 lg:py-1.5"
                  >
                    <span className="relative flex h-7 w-7 shrink-0 items-center justify-center">
                      <img
                        src="https://file.hstatic.net/200000420363/file/cart_57531e43108d48c6ab3281bbbc813beb.png"
                        alt=""
                        width={24}
                        height={22}
                        className="h-5 w-5 object-contain lg:h-[22px] lg:w-[22px]"
                        aria-hidden
                      />
                      <span className="absolute -right-px -top-px flex h-3.5 min-w-[0.875rem] items-center justify-center rounded-full bg-gradient-to-br from-amber-500 to-amber-600 px-0.5 text-[8px] font-bold leading-none text-white ring-[1.5px] ring-white">
                        {totalItems > 99 ? '99+' : totalItems}
                      </span>
                    </span>
                    <span className="hidden whitespace-nowrap text-left text-[13px] font-semibold leading-tight lg:inline">
                      Giỏ hàng
                    </span>
                  </Link>

                  {cartMenuOpen && (
                    <div
                      className="header-cart-dropdown header-hover-surface header-hover-surface--compact absolute right-0 top-full z-[1100] mt-1.5 w-[min(92vw,24rem)] overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_22px_46px_rgba(15,23,42,0.16)]"
                      onMouseEnter={() => {
                        isHoveringCartMenuRef.current = true;
                        clearCloseCartMenuTimer();
                        setCartMenuOpen(true);
                      }}
                      onMouseLeave={() => {
                        isHoveringCartMenuRef.current = false;
                        setCartMenuOpen(false);
                        clearCloseCartMenuTimer();
                      }}
                    >
                      <div className="border-b border-slate-200 bg-gradient-to-r from-amber-50 to-white px-4 py-3">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="text-sm font-bold text-slate-900">Sản phẩm trong giỏ</p>
                            <p className="mt-0.5 text-xs text-slate-500">
                              {totalItems
                                ? `${totalItems} sản phẩm đã thêm`
                                : 'Giỏ hàng đang trống'}
                            </p>
                          </div>
                          <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-bold text-amber-700">
                            {totalItems > 99 ? '99+' : totalItems}
                          </span>
                        </div>
                      </div>

                      {items.length ? (
                        <>
                          <div className="max-h-[22rem] overflow-y-auto px-2 py-2">
                            {items.slice(0, 5).map((item) => (
                              <div
                                key={item.id}
                                className="group flex items-start gap-3 rounded-xl px-2 py-2 transition-colors hover:bg-slate-50"
                              >
                                <Link
                                  href={`/san-pham/${item.slug}`}
                                  className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-white"
                                  onClick={() => setCartMenuOpen(false)}
                                >
                                  {toOptionalUrl(item.image) ? (
                                    <img
                                      src={toOptionalUrl(item.image)!}
                                      alt={item.name}
                                      className="h-full w-full object-contain p-1.5 mix-blend-multiply"
                                    />
                                  ) : (
                                    <span className="text-[11px] text-slate-400">No image</span>
                                  )}
                                </Link>
                                <div className="min-w-0 flex-1">
                                  <Link
                                    href={`/san-pham/${item.slug}`}
                                    onClick={() => setCartMenuOpen(false)}
                                    className="line-clamp-2 text-[13px] font-semibold leading-5 text-slate-800 transition-colors hover:text-amber-700"
                                  >
                                    {item.name}
                                  </Link>
                                  <div className="mt-1 flex items-center justify-between gap-3">
                                    <div className="text-xs text-slate-500">
                                      SL: {item.quantity}
                                    </div>
                                    <div className="text-sm font-extrabold text-red-600">
                                      {formatPrice(item.price * item.quantity)}
                                    </div>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => removeFromCart(item.id)}
                                  className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600"
                                  aria-label={`Xóa ${item.name} khỏi giỏ hàng`}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="14"
                                    height="14"
                                    fill="currentColor"
                                    viewBox="0 0 16 16"
                                  >
                                    <path d="M5.5 5.5A.5.5 0 0 1 6 6v5a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0A.5.5 0 0 1 8.5 6v5a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v5a.5.5 0 0 0 1 0V6z" />
                                    <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1 0-2H5.1a1 1 0 0 1 .95-.684h3.9a1 1 0 0 1 .95.684h2.6a1 1 0 0 1 1 1zM4 4v9a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4H4z" />
                                  </svg>
                                </button>
                              </div>
                            ))}
                          </div>

                          <div className="border-t border-slate-200 bg-slate-50/70 px-4 py-3">
                            <div className="mb-3 flex items-center justify-between gap-3">
                              <span className="text-sm font-semibold text-slate-700">
                                Tổng tạm tính
                              </span>
                              <span className="text-base font-extrabold text-red-600">
                                {formatPrice(totalPrice)}
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <Link
                                href="/gio-hang"
                                onClick={() => setCartMenuOpen(false)}
                                className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:border-amber-300 hover:text-amber-700"
                              >
                                Xem giỏ hàng
                              </Link>
                              <Link
                                href="/thanh-toan"
                                onClick={() => setCartMenuOpen(false)}
                                className="inline-flex items-center justify-center rounded-xl bg-amber-500 px-3 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-amber-600"
                              >
                                Thanh toán
                              </Link>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="px-4 py-8 text-center">
                          <p className="text-sm font-semibold text-slate-700">
                            Giỏ hàng đang trống
                          </p>
                          <p className="mt-1 text-xs text-slate-500">
                            Rê chuột vào sản phẩm và thêm vài món để xem nhanh tại đây.
                          </p>
                          <Link
                            href="/"
                            onClick={() => setCartMenuOpen(false)}
                            className="mt-4 inline-flex items-center justify-center rounded-xl bg-amber-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-amber-600"
                          >
                            Tiếp tục mua sắm
                          </Link>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Mega Menu Dropdown */}
        {menuOpen && (
          <div className="absolute top-full left-0 right-0 z-50 pt-1 md:pt-2 pointer-events-none">
            <div className="max-w-[1500px] mx-auto px-2 md:px-4">
              <div
                ref={megaMenuRef}
                className="header-mega-panel header-hover-surface relative z-10 w-full md:max-w-[1120px] rounded-2xl border border-slate-200/80 bg-[#f7f3ee]/95 shadow-[0_20px_50px_rgba(15,23,42,0.18)] p-3 md:p-5 pointer-events-auto max-h-[72vh] md:max-h-none overflow-y-auto overscroll-contain"
                onMouseEnter={() => {
                  isHoveringMenuRef.current = true;
                  clearCloseMenuTimer();
                  setMenuOpen(true);
                }}
                onMouseLeave={() => {
                  isHoveringMenuRef.current = false;
                  scheduleCloseMenuFromTrigger();
                }}
              >
                <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-5">
                  <div className="rounded-xl border border-slate-200 bg-white/80 max-h-[62vh] overflow-y-auto">
                    {resolvedMegaCategories.map((cat, idx) => {
                      const active = idx === activeMegaIndex;
                      return (
                        <button
                          key={`${cat.label}-${idx}`}
                          type="button"
                          onMouseEnter={() => setActiveMegaIndex(idx)}
                          onFocus={() => setActiveMegaIndex(idx)}
                          className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 text-[15px] text-left transition-colors border-b border-slate-100 last:border-b-0 ${
                            active
                              ? 'bg-slate-100 text-slate-900 font-semibold'
                              : 'text-slate-700 hover:bg-slate-50'
                          }`}
                        >
                          {cat.icon ? (
                            /^https?:\/\//.test(cat.icon) ? (
                              <img
                                src={cat.icon}
                                alt={cat.label}
                                width={18}
                                height={18}
                                className="w-[18px] h-[18px] object-contain flex-shrink-0"
                              />
                            ) : (
                              <AppIcon
                                name={getCatalogIconName(cat.label)}
                                size={19}
                                className="shrink-0 text-slate-500"
                                aria-hidden="true"
                              />
                            )
                          ) : (
                            <AppIcon
                              name={getCatalogIconName(cat.label)}
                              size={19}
                              className="shrink-0 text-slate-500"
                              aria-hidden="true"
                            />
                          )}
                          <span>{cat.label}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white/80 p-4 min-h-[220px] max-h-[360px] overflow-y-auto">
                    {resolvedMegaCategories[activeMegaIndex] ? (
                      <>
                        <div className="rounded-xl border border-amber-100 bg-amber-50/70 px-4 py-3">
                          <Link
                            href={resolvedMegaCategories[activeMegaIndex].href || '#'}
                            onClick={() => setMenuOpen(false)}
                            className="text-base font-semibold text-slate-800 hover:text-primary"
                          >
                            {resolvedMegaCategories[activeMegaIndex].label}
                          </Link>
                          <div className="mt-1 text-xs text-slate-600">
                            {resolvedMegaCategories[activeMegaIndex].sub?.length
                              ? 'Chọn mục con bên dưới để xem nhanh đúng nhóm sản phẩm.'
                              : 'Danh mục tổng hợp, bấm vào tiêu đề để xem toàn bộ sản phẩm.'}
                          </div>
                        </div>
                        {(() => {
                          const subs = resolvedMegaCategories[activeMegaIndex].sub || [];
                          const groups = buildGroupedSubs(
                            resolvedMegaCategories[activeMegaIndex].label,
                            subs
                          );
                          if (!subs.length) {
                            return null;
                          }
                          if (groups && groups.length) {
                            return (
                              <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                                {groups.map((group) => (
                                  <Link
                                    key={group.title}
                                    href={getMegaSubHref(
                                      resolvedMegaCategories[activeMegaIndex].href || '#',
                                      group.items[0] || group.title
                                    )}
                                    onClick={() => setMenuOpen(false)}
                                    className="rounded-lg border border-slate-100 bg-white/75 px-3 py-2"
                                  >
                                    <div className="text-[15px] font-semibold text-slate-700">
                                      {group.title}
                                    </div>
                                  </Link>
                                ))}
                              </div>
                            );
                          }
                          return (
                            <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2">
                              {subs.map((sub) => (
                                <Link
                                  key={`${resolvedMegaCategories[activeMegaIndex].label}-${sub}`}
                                  href={getSubCategoryHref(
                                    resolvedMegaCategories[activeMegaIndex].href || '#',
                                    sub
                                  )}
                                  onClick={() => setMenuOpen(false)}
                                  className="rounded-lg border border-slate-100 bg-white/75 px-3 py-2 text-sm text-slate-600 hover:text-primary hover:underline"
                                >
                                  {sub}
                                </Link>
                              ))}
                            </div>
                          );
                        })()}
                      </>
                    ) : (
                      <div className="text-sm text-slate-500">Chưa có dữ liệu danh mục</div>
                    )}
                  </div>
                </div>
                {!resolvedMegaCategories.length && (
                  <div className="py-5 text-sm text-slate-500 text-center">
                    Chưa có dữ liệu danh mục
                  </div>
                )}
                <div className="mt-3 pt-3 border-t border-slate-200/80 grid grid-cols-1 md:grid-cols-3 gap-2">
                  {[
                    { label: 'Blog / tin tức', href: '/#blogHome' },
                    { label: 'Xây dựng cấu hình', href: '/xay-dung-cau-hinh' },
                    { label: 'Lịch vệ sinh máy', href: '/lien-he' },
                    { label: 'Tra cứu bảo hành', href: '/tra-cuu-bao-hanh' },
                  ].map((box) => (
                    <Link
                      key={box.label}
                      href={box.href}
                      onClick={() => setMenuOpen(false)}
                      className="block rounded-xl border border-slate-200 bg-white/80 px-3 py-2 text-sm font-medium text-slate-700 hover:border-primary/50 hover:text-primary transition-colors"
                    >
                      {box.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </header>
      {mounted && (menuOpen || topNavDropdownOpen || userMenuOpen || cartMenuOpen)
        ? createPortal(
            <button
              type="button"
              aria-label={
                menuOpen
                  ? 'Đóng danh mục sản phẩm'
                  : userMenuOpen || cartMenuOpen
                    ? 'Đóng menu tài khoản và giỏ hàng'
                    : 'Đóng menu nhanh'
              }
              className="header-mega-backdrop header-hover-backdrop fixed inset-0 z-[990] bg-[rgba(2,6,23,0.56)]"
              onClick={() => {
                setMenuOpen(false);
                setTopNavDropdownOpen(null);
                setUserMenuOpen(false);
                setCartMenuOpen(false);
              }}
            />,
            headerRef.current?.closest('.site-shell') || document.body
          )
        : null}
      <div
        aria-hidden="true"
        className={`header-spacer shrink-0 ${
          mobileSearchHidden ? 'header-spacer--mobile-search-hidden' : ''
        }`}
        style={{ height: `${mobileSearchHidden ? 72 : headerHeight}px` }}
      />
    </>
  );
}
